/**
 * Shared types for the new roster ingestion flow:
 *  Upload → Column Mapping → Validation → Load
 */

// ─── Standard Roster Fields ──────────────────────────────────────────────────

/** The canonical set of target fields every roster file is mapped to. */
export const ROSTER_STANDARD_FIELDS = [
  'TIN',
  'NPI',
  'BILLING_ADDRESS_1',
  'BILLING_CITY',
  'BILLING_STATE',
  'BILLING_PHONE_NUMBER',
  'BILLING_ZIP',
  'ADDRESS_1',
  'CITY',
  'STATE',
  'ZIP_CODE',
  'PHONE',
  'FIRST_NAME',
  'LAST_NAME',
  'PRIMARY_DEGREE',
  'PRIMARY_SPECIALTY',
] as const;

export type RosterStandardField = typeof ROSTER_STANDARD_FIELDS[number];

// ─── Column Mapping ──────────────────────────────────────────────────────────

export interface ColumnMappingEntry {
  id: string;
  sourceCol: string;
  systemField: string;      // e.g. 'MEMBER_ID', 'TIN', 'IGNORE', …
  sampleValues: string[];
  isAutoMapped: boolean;    // true = API/local auto-mapped, false = user-edited
  confidenceScore?: number; // 0–1 from the AI model
  requiresReview?: boolean; // true = model flagged for manual review
}

// ─── Validation ──────────────────────────────────────────────────────────────

export interface ValidationIssue {
  field: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface ValidationRecord {
  rowNumber: number;
  data: Record<string, string>;
  status: 'valid' | 'error' | 'warning';
  issues: ValidationIssue[];
  /** Full 200 response from the validation agent — may contain enriched/derived fields. */
  enrichedPayload?: Record<string, any>;
}

export interface ValidationResult {
  totalRows: number;
  validRows: number;
  errorRows: number;
  warningRows: number;
  records: ValidationRecord[];
}

// ─── Load ────────────────────────────────────────────────────────────────────

export interface LoadRecord {
  rowNumber: number;
  memberId: string;
  status: 'loaded' | 'failed' | 'skipped';
  message?: string;
}

export interface LoadResult {
  totalRecords: number;
  loadedRecords: number;
  failedRecords: number;
  skippedRecords: number;
  durationMs: number;
  records: LoadRecord[];
}
